// Eskom PARCC — resolve which per-mechanism working paper applies to a
// transaction, and the rule-book compliance suggestion for a criterion.
//
// A transaction's DIVISION picks the workbook family (NTCSA vs Other Divisions);
// its MECHANISM (normalised from the 40+ raw variants) picks the sheet. The
// suggestion for a criterion is its workbook default mapped to a register level
// (undefined for caveat criteria whose hint is a free-text "Depends…" — the
// caller then falls back to the text classifier).
import type { DivisionFamily, WorkingPaperTemplate, WpCriterion } from './templateTypes';
import { WORKING_PAPER_TEMPLATES } from './workingPaperTemplates.generated';
import { normalizeDefaultLevel, registerLevelFor } from './terminology';
import type { ComplianceLevel } from '../enums';

/** NTCSA division → NTCSA workbook; everything else → Other Divisions. */
export function divisionFamily(division?: string | null): DivisionFamily {
  return /ntcsa/i.test(String(division ?? '')) ? 'NTCSA' : 'Other';
}

/**
 * Map a raw mechanism (+ value fallback) to the canonical sheet key available in
 * the given family, or undefined when the family has no matching sheet.
 */
export function normalizeMechanism(
  mechanism: string | undefined | null,
  value: number | undefined,
  family: DivisionFamily,
): string | undefined {
  const m = String(mechanism ?? '').toLowerCase();
  // Both families provide a "Formal Tendering - Contracts" sheet — the non-NTCSA
  // workbook dropped its Informal-Contracts sheet in the Jul 2026 refresh — so the
  // contracts route resolves there for everyone. (The Informal→Formal fallback
  // below is kept defensively for any family that still ships the old sheet.)
  const contracts = 'Formal Tendering - Contracts';
  let key: string | undefined;
  if (/sole source/.test(m)) key = 'Sole Source';
  else if (/single source/.test(m)) key = 'Single Source';
  else if (/multiple source/.test(m)) key = 'Multiple Sources';
  else if (/emergency/.test(m)) key = 'Emergency';
  else if (/urgent/.test(m)) key = 'Urgent';
  else if (/task order/.test(m)) key = 'Task Order';
  else if (/transvers|transves/.test(m)) key = 'Transversal';
  else if (/modification|expansion|contract adjustment|conversion/.test(m)) key = 'Expansions - Modifications';
  else if (/lpo|local purchase/.test(m)) key = 'LPO';
  else if (/informal|standard po|rfq|request for quotation|emergency po/.test(m)) key = "Informal Tendering - PO's";
  else if (/formal|open tender|closed tender|limited tender|tender|panel/.test(m)) key = contracts;
  else {
    const v = Number(value ?? 0);
    if (v > 0 && v <= 30000) key = 'LPO';
    else if (v > 0 && v <= 1_000_000) key = "Informal Tendering - PO's";
    else key = contracts;
  }
  const fam = WORKING_PAPER_TEMPLATES[family];
  if (key && fam[key]) return key;
  // Contracts-route fallback to whichever contracts sheet the family has.
  if (key === 'Formal Tendering - Contracts' && fam['Informal Tendering - Contracts']) {
    return 'Informal Tendering - Contracts';
  }
  if (key === 'Informal Tendering - Contracts' && fam['Formal Tendering - Contracts']) {
    return 'Formal Tendering - Contracts';
  }
  return undefined;
}

/** Resolve the working-paper template for a transaction (undefined → use the generic CES route picker). */
export function resolveWorkingPaperTemplate(tx: {
  division?: string | null;
  mechanism?: string | null;
  value?: number;
}): WorkingPaperTemplate | undefined {
  const family = divisionFamily(tx.division);
  const key = normalizeMechanism(tx.mechanism, tx.value, family);
  return key ? WORKING_PAPER_TEMPLATES[family][key] : undefined;
}

/**
 * The rule-book suggested compliance level for one criterion: its workbook
 * default mapped to the register vocabulary. Undefined when the criterion has no
 * default (NTCSA) or only a free-text caveat — the caller then falls back to the
 * text classifier.
 */
export function suggestComplianceForCriterion(c: WpCriterion): ComplianceLevel | undefined {
  const canonical = normalizeDefaultLevel(c.defaultLevelRaw);
  return canonical ? registerLevelFor(canonical) : undefined;
}
